import { useSyncExternalStore } from 'react';
import * as Y from 'yjs';
import { canvasRoots } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';

/**
 * Subscribes to a node's `locked` flag in Yjs. Returns the current
 * value and re-renders the consumer when the node mutates.
 *
 * Returns false when:
 *  - no doc is provided
 *  - the node doesn't exist
 *  - the node has no `locked` field set
 *
 * Surfaces (selection chrome, right-click menu, keyboard shortcuts)
 * read locked-state through this hook so the UI stays in sync with
 * collaborator-driven lock toggles without re-rendering the entire
 * canvas tree on every Yjs event.
 */
export function useYjsLocked(doc: Y.Doc | undefined, nodeId: string): boolean {
  const binding = useCanvasBinding();
  return useSyncExternalStore(
    (onChange) => {
      if (!doc) return () => {};
      const node = canvasRoots(doc, binding).nodes.get(nodeId);
      if (!node) return () => {};
      node.observe(onChange);
      return () => node.unobserve(onChange);
    },
    () => {
      if (!doc) return false;
      const node = canvasRoots(doc, binding).nodes.get(nodeId);
      return node?.get('locked') === true;
    },
  );
}
